import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  OnDestroy,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivityTypeEnum, helperTypeActivity } from '@app/core/models';
import { ActivityService, AlertService } from '@app/core/services';
import { Resp } from '@app/core/services/api.service';
import { AttachmentService } from '@app/core/services/attachment.service';
import { SHARED_MODULES } from '@app/shared';
import { TextEditorComponent } from '@app/shared/modules/text-editor/text-editor.component';
import { notNull } from '@app/shared/utils';
import { AppSelectors } from '@app/store';
import { Popover, Strategy } from '@app/shared/modules/template/components/popover';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { endOfDay, format, max, parse, startOfDay } from 'date-fns';
import { isEmpty, isNil, isObject } from 'lodash';
import { DropzoneConfigInterface } from 'ngx-dropzone-wrapper';
import { BehaviorSubject, catchError, combineLatest, Observable, of, startWith, Subject } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { AttachmentDropzoneComponent, ExistingAttachment } from '../attachment-dropzone/attachment-dropzone.component';
import { UploadFile } from '../attachment-dropzone/dropzone.types';
import { ListSpecificStudentsComponent } from '../list-specific-students/list-specific-students.component';
import { DAILY_TEXT_EDITOR_URL, DAILY_TOOLBAR, DAILY_UPLOAD, FormBase, ModalDailyTab, SidenavDailyShow } from './base';
import { EvaluationType } from './sidenav-evaluation.component';
import { PopoverActivityComponent, TypeHelpActivityEnum } from './popover-activity/popover-activity.component';
@Component({
  imports: [SHARED_MODULES, TextEditorComponent, AttachmentDropzoneComponent, ListSpecificStudentsComponent],
  templateUrl: './form-activity.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormActivityComponent extends FormBase implements AfterViewInit, OnDestroy {
  private readonly _destroy = inject(DestroyRef);
  private readonly _cdref = inject(ChangeDetectorRef);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _alertService = inject(AlertService);
  private readonly _activityService = inject(ActivityService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _popover = inject(Popover);
  public readonly strategy = Strategy;
  public override title = '';
  public override titleElement = '';
  public override targetsLabel = 'eclass.tarefa.cadastrar-para';
  public override publishLabel = 'eclass.tarefa.publicar';
  public override submitted$: Observable<boolean>;
  public override type = ModalDailyTab.Activity;
  public override form: FormGroup;
  public override showSidenavEnd = false;
  public override showSidenavDaily = false;
  public override sidenavEndData: any;
  public readonly textEditorUrl = DAILY_TEXT_EDITOR_URL;
  public readonly toolbar = DAILY_TOOLBAR;
  public TypeHelpActivityEnum = TypeHelpActivityEnum;
  public targets$: Observable<SidenavDailyShow[]>;
  public date$ = new Subject<Date>();
  public isExternalHispanic: boolean;
  public config: DropzoneConfigInterface = { ...DAILY_UPLOAD, params: { folder: 'activities' } };
  public isLegacy = signal(false);
  private _submitted$ = new BehaviorSubject<boolean>(false);
  private _targets$ = new BehaviorSubject<SidenavDailyShow[]>([]);

  constructor() {
    super();
    this._store
      .select(AppSelectors.isExternalHispanic)
      .pipe(notNull(), take(1))
      .subscribe((res) => (this.isExternalHispanic = res));
    this.date$.next(startOfDay(new Date()));
    this.targets$ = this._targets$.asObservable();
    this.submitted$ = this._submitted$.asObservable();
    this.initForm();
  }

  public override set targets(value: SidenavDailyShow[]) {
    this._targets$.next(value);
    if (value.length) {
      const maxDate = max(value.map((daily) => daily.date));
      if (maxDate > this.form.value.date_end) {
        this.form.get('date_end').reset();
      }
      this.date$.next(maxDate);
    }
  }

  public override setLegacy(value: boolean) {
    this.isLegacy.set(value);
  }

  public onOpenHelpText(elementRef: any, typeHelp: TypeHelpActivityEnum): void {
    this._popover.open(PopoverActivityComponent, {
      positionStrategy: [Strategy.BOTTOM, Strategy.LEFT],
      elementRef,
      data: typeHelp,
    });
  }

  private get attachments(): FormControl {
    return this.form.get('attachments') as FormControl;
  }

  public get existingAttachments(): ExistingAttachment[] {
    const attachments = this.attachments.value ?? [];

    return attachments
      .filter((att: any) => att.id !== null)
      .map((att: any) => ({
        id: att.id,
        name: att.title ?? att.file,
        size: att.size ?? 0,
        url: att.url,
        mimeType: att.mime_type,
      }));
  }

  public ngAfterViewInit() {
    const evaluative = this.form.get('evaluative');
    evaluative.valueChanges
      .pipe(takeUntilDestroyed(this._destroy), startWith(evaluative.value))
      .subscribe((value) => (this.showSidenavEnd = value));
  }

  public ngOnDestroy() {
    this._submitted$.complete();
    this._targets$.complete();
  }

  public onFileRemoved(targetId: string) {
    const targetFile = this.attachments.value.find(
      (att: any) => String(att.id) === targetId || att.dropzoneId === targetId,
    );

    const updateForm = () => {
      const files = this.attachments.value.filter(
        (att: any) => String(att.id) !== targetId && att.dropzoneId !== targetId,
      );
      this.attachments.setValue(files);
    };

    if (targetFile?.id) {
      this._attachmentService.deleteAttachment(targetFile.id).subscribe((res) => {
        if (res.ret) {
          this._alertService.success({ message: 'geral.excluido-sucesso' });
        } else {
          this._alertService.alert({ message: res.msg || 'geral.excluido-erro' });
        }
        updateForm();
      });
    } else {
      updateForm();
    }
  }

  public onUploaded(event: UploadFile) {
    const file = {
      id: null,
      dropzoneId: event.id,
      file: event.uploadedFile,
      title: event.name,
      size: event.size,
      extension: event.name.split('.').pop()?.toLowerCase(),
      key: event.key,
      url: null,
    };
    this.attachments.setValue([...(this.attachments.value || []), file]);
    this.attachments.markAsDirty();
    this.attachments.markAsTouched();
  }

  public onFilesChanged(files: UploadFile[]) {
    const isUploading = files.some((f) => f.status === 'uploading' || f.status === 'waiting');
    this._submitted$.next(isUploading);
  }

  public onSelectAllStudents(allSelect: boolean) {
    this.form.get('allStudents').setValue(allSelect);
  }

  public override markAllAsTouched() {
    this.form.markAllAsTouched();
    this._cdref.markForCheck();
  }

  public override patchValue(data: any, target: SidenavDailyShow) {
    this.form.patchValue(
      {
        ...data,
        date_end: data.date_end ? parse(data.date_end, 'yyyy-MM-dd HH:mm:ss', new Date()) : null,
        limit: !data.limit,
        evaluative: !!data.evaluative,
        submit: [ActivityTypeEnum.AssessmentOnline, ActivityTypeEnum.TaskOnline].includes(data.type),
        specificStudents: !isEmpty(data.students),
        allStudents: isEmpty(data.students),
        type: data.scale_id ? EvaluationType.Scale : EvaluationType.Score,
        score: data.scale_id || Number(data.score),
        students: data.students.map(({ id }) => ({ studentId: id, groupId: target.group.id })),
        learningTrailsElements: data.trail_elements,
        games: data.games ? data.games.map((g) => g.id) : [],
      },
      { emitEvent: false },
    );

    this.form.markAsPristine();
    this.form.markAsUntouched();
    this._cdref.markForCheck();
  }

  public override resetForm() {
    this.form.reset({
      ...this.form.value,
      title: null,
      description: null,
      attachments: [],
    });
    this.form.markAsPristine();
    this.form.markAsUntouched();
    this._cdref.markForCheck();
  }

  public override onSave(params: any): Observable<boolean> {
    return this._alertService
      .confirm({ message: 'eclass.tarefa.salvar' })
      .pipe(switchMap((confirm) => (confirm ? this.save(params) : of(false))));
  }

  private save(params: any): Observable<boolean> {
    const targets = params.targets.map((target: SidenavDailyShow) => ({
      id: target.id,
      entity_id: target.entity.id,
      group_id: target.group.id,
      subject_id: target.subject.id,
      date: format(target.date, 'yyyy-MM-dd'),
      students: this.helperStudents(target.group.id, params.form),
    }));
    const data = {
      type: helperTypeActivity(params.form),
      limit: !params.form.limit,
      evaluative: params.form.evaluative,
      title: params.form.title,
      date_end: format(params.form.date_end, 'yyyy-MM-dd HH:mm'),
      description: params.form.description,
      attachments: params.form.attachments,
      scale_id: params.form.evaluative && params.form.type === EvaluationType.Scale ? params.form.score : undefined,
      score: params.form.evaluative && params.form.type === EvaluationType.Score ? params.form.score : undefined,
      published: params.publish,
      learningTrailsElements: params.form.learningTrailsElements,
      games: params.form.games,
    };
    const store$ = this.store(targets, data);
    const update$ = this.update(targets, data);
    return combineLatest([store$, update$]).pipe(
      take(1),
      map(([store, update]) => store && update),
      tap((result) => !!result && this._alertService.success({ message: 'geral.salvo-com-sucesso' })),
    );
  }

  private helperStudents(groupId: number, data: any): number[] {
    if (data.allStudents || !data.specificStudents) {
      return [];
    }
    return data.students.filter((student) => student.groupId === groupId).map(({ studentId }) => studentId);
  }

  private store(params: SidenavDailyShow[], data: any): Observable<boolean> {
    const target = params.filter(({ id }) => isNil(id)).map(({ id, ...target }) => target);
    if (!isEmpty(target)) {
      return this.handleResponse(
        this._activityService.store({ ...data, attachments: data?.attachments.map(({ id, ...a }) => a), target }),
      );
    }
    return of(true);
  }

  private update(params: SidenavDailyShow[], data: any): Observable<boolean> {
    const target = params.find(({ id }) => !isNil(id));
    if (!!target) {
      return this.handleResponse(this._activityService.update(target.id, { ...data, ...target }));
    }
    return of(true);
  }

  private handleResponse(observable$: Observable<Resp<any>>): Observable<boolean> {
    return observable$.pipe(
      catchError((data) => {
        const message = data.msg || data.error?.msg;
        return of({ ret: 0, data: isObject(message) ? JSON.stringify(message) : message });
      }),
      switchMap((result) => {
        if (!result.ret) {
          const message = !!result.data ? { innerHtml: result.data } : { message: 'geral.erro-ao-salvar' };
          return this._alertService.alert(message).pipe(map(() => false));
        }
        return of(true);
      }),
    );
  }

  private initForm() {
    this.form = this._formBuilder.group({
      title: [null, [Validators.required, Validators.maxLength(100)]],
      description: null,
      date_end: [endOfDay(new Date()), Validators.required],
      evaluative: false,
      limit: false,
      submit: false,
      specificStudents: false,
      allStudents: true,
      type: null,
      score: [null, [Validators.required, Validators.min(0.1)]],
      students: [[]],
      attachments: [[]],
      learningTrailsElements: [[]],
      games: [[]],
    });

    this.form
      .get('evaluative')
      .valueChanges.pipe(takeUntilDestroyed(this._destroy), startWith(this.form.value.evaluative))
      .subscribe((value) => {
        if (value) {
          this.form.get('score').enable();
        } else {
          this.form.get('score').disable();
        }
      });
  }
}
